<template>
  <div class="ovg-page">
    <div class="ovg-page__bg" aria-hidden="true" />

    <header class="ovg-header">
      <div
        class="ovg-header__logos"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        <img
          src="@/assets/logo_original.svg"
          alt="Samtek"
          class="ovg-header__logo-samtek"
        />
        <img
          src="@/assets/logo_asus.png"
          alt="ASUS"
          class="ovg-header__logo-asus"
        />
      </div>
      <button
        class="ovg-header__salir"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        Salir <font-awesome-icon icon="sign-out-alt" />
      </button>
    </header>

    <div class="ovg-subheader">
      <h2 class="ovg-subheader__title">
        Adquiere tu Extensión de Garantía Oficial
      </h2>
      <p class="ovg-subheader__subtitle">
        Revisa el resumen de tu pedido y confirma la compra.
      </p>
    </div>

    <!-- Estado: carrito (default) — layout 2 columnas -->
    <div v-if="inlineState === 'carrito'" class="ovg-carrito-wrapper">
      <!-- Columna izquierda: Resumen Solicitud -->
      <div class="ovg-card ovg-carrito-left">
        <div class="ovg-textos">
          <h3 class="ovg-textos__title">Resumen Solicitud</h3>
          <p class="ovg-textos__subtitle">
            Revisa los detalles de tu garantía seleccionada y envía tu
            solicitud. Ventas revisará tu solicitud antes de aprobar la compra.
          </p>
        </div>

        <!-- Datos cliente y equipo -->
        <div class="ovg-datos">
          <div class="ovg-datos__field ovg-datos__field--full">
            <span class="ovg-datos__label ovg-datos__label--accent">R.U.T</span>
            <span class="ovg-datos__value">{{ cliente.rut }}</span>
          </div>
          <div class="ovg-datos__field">
            <span class="ovg-datos__label">Nombre</span>
            <span class="ovg-datos__value">{{ cliente.nombre }}</span>
          </div>
          <div class="ovg-datos__field">
            <span class="ovg-datos__label">Apellido</span>
            <span class="ovg-datos__value">{{ cliente.apellido }}</span>
          </div>
          <div class="ovg-datos__field">
            <span class="ovg-datos__label">Correo Electrónico</span>
            <span class="ovg-datos__value">{{ cliente.correo }}</span>
          </div>
          <div class="ovg-datos__field">
            <span class="ovg-datos__label">Teléfono</span>
            <span class="ovg-datos__value">{{ cliente.telefono || '—' }}</span>
          </div>
          <div class="ovg-datos__field">
            <span class="ovg-datos__label ovg-datos__label--accent"
              >N° De Serie Equipo</span
            >
            <span class="ovg-datos__value">{{ equipo.numero_serie }}</span>
          </div>
          <div class="ovg-datos__field">
            <span class="ovg-datos__label">Modelo del Equipo</span>
            <span class="ovg-datos__value">{{ equipo.modelo_nombre }}</span>
          </div>
        </div>

        <!-- Productos seleccionados (extensión + protecciones) -->
        <div v-for="p in productos" :key="p.id" class="ovg-producto-row">
          <svg
            class="ovg-producto-row__icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2L3 7v5c0 5.25 3.83 10.16 9 11.35C17.17 22.16 21 17.25 21 12V7L12 2z"
              fill="#22c55e"
            />
            <path
              d="M9 12l2 2 4-4"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <div class="ovg-producto-row__info">
            <span class="ovg-producto-row__nombre">{{ p.nombre }}</span>
            <span class="ovg-producto-row__precio">{{
              formatPrecio(p.precio)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Columna derecha: Resumen Pago -->
      <div class="ovg-card ovg-carrito-right">
        <h3 class="ovg-resumen-pago__title">Resumen Pago</h3>

        <!-- Sección cupón (OVG03.2 — SILL-1523) -->
        <div class="ovg-cupon">
          <div class="ovg-cupon__row">
            <input
              v-model="codigoCupon"
              type="text"
              class="ovg-cupon__input"
              placeholder="Código de Descuento"
              :disabled="cuponBloqueado || cuponLoading"
              @keyup.enter="!cuponBloqueado && aplicarCupon()"
            />
            <button
              type="button"
              class="ovg-btn ovg-btn--dark ovg-cupon__btn"
              :disabled="
                (!cuponBloqueado && !codigoCupon.trim()) || cuponLoading
              "
              @click="cuponBloqueado ? limpiarCupon() : aplicarCupon()"
            >
              <span v-if="cuponLoading">...</span>
              <span v-else-if="cuponBloqueado">Eliminar</span>
              <span v-else>Aplicar</span>
            </button>
          </div>
          <!-- Feedback éxito -->
          <p
            v-if="tieneCupon"
            class="ovg-cupon__feedback ovg-cupon__feedback--ok"
          >
            El código de descuento se aplicó correctamente
          </p>
          <!-- Feedback error -->
          <p
            v-else-if="cuponError"
            class="ovg-cupon__feedback ovg-cupon__feedback--error"
          >
            {{ cuponError }}
          </p>
        </div>

        <!-- Totales -->
        <div class="ovg-totales">
          <div class="ovg-totales__row">
            <span class="ovg-totales__label">Ítems:</span>
            <span class="ovg-totales__value">{{ formatPrecio(subtotal) }}</span>
          </div>
          <div class="ovg-totales__row">
            <span class="ovg-totales__label">Descuentos:</span>
            <span class="ovg-totales__value"
              >{{ descuento > 0 ? '-' : '' }}{{ formatPrecio(descuento) }}</span
            >
          </div>
          <div class="ovg-totales__row ovg-totales__row--total">
            <span class="ovg-totales__label--total">Total:</span>
            <span class="ovg-totales__value--total">{{
              formatPrecio(total)
            }}</span>
          </div>
        </div>

        <!-- Aviso solicitud -->
        <div class="ovg-info-aviso">
          <font-awesome-icon icon="info-circle" class="ovg-info-aviso__icon" />
          <p class="ovg-info-aviso__text">
            Al pulsar <strong>"Solicitar compra"</strong>, estás enviando una
            solicitud. No se realizará ningún cobro en este momento. Recibirás
            un enlace de pago por correo electrónico una vez aprobada tu
            solicitud.
          </p>
        </div>

        <div v-if="errorCompra" class="ovg-error-msg">
          <font-awesome-icon icon="exclamation-triangle" />
          {{ errorMsg }}
        </div>

        <div class="ovg-card__footer ovg-card__footer--stack">
          <button
            type="button"
            class="ovg-btn ovg-btn--primary ovg-btn--block ovg-btn--solicitar"
            :disabled="confirmando"
            @click="confirmarCompra"
          >
            {{ confirmando ? 'Procesando...' : 'Solicitar compra' }}
          </button>
          <button
            type="button"
            class="ovg-link-text"
            @click="$router.push({ name: 'ovg-catalogo' })"
          >
            Volver al catálogo
          </button>
        </div>
      </div>
    </div>

    <!-- Estado: solicitud-recibida -->
    <div
      v-else-if="inlineState === 'solicitud-recibida'"
      class="ovg-card ovg-card--narrow"
    >
      <font-awesome-icon
        icon="check-circle"
        class="ovg-estado__icon ovg-estado__icon--success ovg-exito__icon"
      />
      <div class="ovg-exito__textos">
        <h3 class="ovg-exito__titulo">¡Solicitud Recibida Exitosamente!</h3>
        <p class="ovg-exito__texto">
          Gracias por confiar en Samtek. Estamos procesando tu solicitud de
          extensión de garantía para el equipo con S/N
          <strong class="ovg-exito__serie">{{
            resumenConfirmacion.numero_serie
          }}</strong>
        </p>
      </div>
      <div class="ovg-exito__textos">
        <h4 class="ovg-exito__subtitulo">Próximos Pasos:</h4>
        <ul class="ovg-exito__lista">
          <li>Nuestro equipo de ventas revisará tu boleta.</li>
          <li>
            Te enviaremos un correo electrónico con el enlace de pago una vez
            que tu solicitud sea aprobada.
          </li>
          <li>Por favor, revisa tu bandeja de entrada y la carpeta de spam.</li>
        </ul>
      </div>
      <button
        type="button"
        class="ovg-btn ovg-btn--dark ovg-btn--block"
        @click="volverAlInicio"
      >
        Volver al inicio
      </button>
    </div>

    <!-- Estado: garantia-activa -->
    <div
      v-else-if="inlineState === 'garantia-activa'"
      class="ovg-card ovg-card--narrow ovg-card--centrado"
    >
      <svg
        class="ovg-estado__icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2L3 7v5c0 5.25 3.83 10.16 9 11.35C17.17 22.16 21 17.25 21 12V7L12 2z"
          fill="#22c55e"
          fill-opacity="0.2"
        />
        <path
          d="M12 2L3 7v5c0 5.25 3.83 10.16 9 11.35C17.17 22.16 21 17.25 21 12V7L12 2z"
          stroke="#22c55e"
          stroke-width="2"
          stroke-linejoin="round"
        />
        <path
          d="M9 12l2 2 4-4"
          stroke="#22c55e"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <h3 class="ovg-estado__titulo">Ya tienes una garantía activa</h3>
      <p class="ovg-estado__texto">
        Ya existe una garantía activa para el N° de serie ingresado. No es
        necesario solicitar otra para este equipo.
      </p>
      <div class="ovg-info-box ovg-info-box--centro">
        <div class="ovg-info-box__row">
          <span class="ovg-info-box__label">Equipo</span>
          <span class="ovg-info-box__value">{{
            equipo ? equipo.modelo_nombre : ''
          }}</span>
        </div>
        <div class="ovg-info-box__row">
          <span class="ovg-info-box__label">N° de serie</span>
          <span class="ovg-info-box__value">{{
            equipo ? equipo.numero_serie : ''
          }}</span>
        </div>
      </div>
      <button
        type="button"
        class="ovg-btn ovg-btn--dark"
        @click="volverAlInicio"
      >
        Volver al inicio
      </button>
    </div>

    <p class="ovg-page__footer">
      © 2026 Samtek | Servicio Técnico ASUS Autorizado en Chile.
    </p>
  </div>
</template>

<script>
export default {
  name: 'CarritoView',
  data() {
    return {
      inlineState: 'carrito',
      confirmando: false,
      errorCompra: false,
      errorMsg: '',
      codigoCupon: '',
      // Snapshot para la confirmación (el store se limpia tras el 201).
      resumenConfirmacion: { numero_serie: '' },
    }
  },
  computed: {
    cliente() {
      return this.$store.getters['OVGStore/cliente'] || {}
    },
    equipo() {
      return this.$store.getters['OVGStore/equipo'] || {}
    },
    extension() {
      return this.$store.getters['OVGStore/extension']
    },
    protecciones() {
      return this.$store.getters['OVGStore/protecciones'] || []
    },
    // Extensión (si hay) + protecciones, para renderizar el resumen.
    productos() {
      const items = []
      if (this.extension) items.push(this.extension)
      return items.concat(this.protecciones)
    },
    // OVG03.2 — Cupón (SILL-1523)
    cupon() {
      return this.$store.getters['OVGStore/cupon']
    },
    cuponLoading() {
      return this.$store.getters['OVGStore/cuponLoading']
    },
    cuponError() {
      return this.$store.getters['OVGStore/cuponError']
    },
    tieneCupon() {
      return this.$store.getters['OVGStore/tieneCupon']
    },
    /** Input bloqueado (no editable) cuando el usuario ya envió el código */
    cuponBloqueado() {
      return this.tieneCupon || this.cuponError !== null
    },
    subtotal() {
      return this.productos.reduce(
        (acc, p) => acc + parseFloat(p.precio || 0),
        0
      )
    },
    descuento() {
      return this.$store.getters['OVGStore/montoCupon'](this.subtotal)
    },
    total() {
      return Math.max(0, this.subtotal - this.descuento)
    },
  },
  created() {
    // Debe haber al menos un producto (extensión o protección) y fecha.
    if (
      this.productos.length === 0 ||
      !this.$store.getters['OVGStore/fechaCompraEquipo']
    ) {
      this.$router.replace({ name: 'ovg-catalogo' })
    }
  },
  methods: {
    aplicarCupon() {
      const codigo = this.codigoCupon.trim()
      if (!codigo) return
      this.$store.dispatch('OVGStore/validarCuponGarantia', codigo)
    },
    limpiarCupon() {
      this.codigoCupon = ''
      this.$store.dispatch('OVGStore/limpiarCupon')
    },
    confirmarCompra() {
      if (this.productos.length === 0) return
      this.confirmando = true
      this.errorCompra = false
      this.errorMsg = ''
      // POST /solicitudes/ crea la OVG en estado 'solicitado'. El pago se
      // realiza luego desde el enlace que el equipo envía por correo tras
      // aprobar la solicitud (PagoView, vía payment_token).
      this.$store
        .dispatch('OVGStore/solicitarCompra')
        .then(() => {
          // Snapshot antes de limpiar el store (la confirmación lo muestra).
          this.resumenConfirmacion = {
            numero_serie: this.equipo.numero_serie,
          }
          this.$store.commit('OVGStore/RESET_STATE')
          this.inlineState = 'solicitud-recibida'
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 409) {
            // Ya existe una garantía activa para ese N° de serie: bloqueo.
            this.inlineState = 'garantia-activa'
          } else {
            this.errorCompra = true
            this.errorMsg =
              'Ocurrió un error al procesar tu solicitud. Por favor intenta nuevamente.'
          }
        })
        .finally(() => {
          this.confirmando = false
        })
    },
    volverAlInicio() {
      this.$router.push({ name: 'ovg-landing' })
    },
    formatPrecio(precio) {
      const num = parseFloat(precio)
      if (Number.isNaN(num)) return precio
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
      }).format(num)
    },
  },
}
</script>

<style scoped>
.ovg-page {
  font-family: 'Noto Sans', sans-serif;
  min-height: 100vh;
  background-color: #f5f6fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 80px;
}

.ovg-page__bg {
  /* Sin `opacity`: el PNG ya es una marca de agua (su patrón viene con
     alpha 13/255). Desvanecerlo otra vez acá lo dejaba en 0,62% de
     contraste, o sea invisible en pantalla. */
  position: fixed;
  inset: 0;
  background-image: url('~@/assets/fondo_samtek.png');
  background-size: cover;
  background-position: center;
  pointer-events: none;
  z-index: 0;
}

.ovg-header {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 55px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 162px;
  flex-shrink: 0;
}

.ovg-header__logos {
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
}

.ovg-header__logo-samtek {
  height: 18px;
  width: auto;
}

.ovg-header__logo-asus {
  height: 18px;
  width: auto;
}

.ovg-header__salir {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #23201c;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.ovg-subheader {
  position: relative;
  z-index: 1;
  width: 100%;
  background: #0a192f;
  min-height: 195px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 20px;
  text-align: center;
}

.ovg-subheader__title {
  font-size: 28px;
  font-weight: 700;
  color: #f5f6fa;
  margin: 0;
}

.ovg-subheader__subtitle {
  font-size: 15px;
  font-weight: 400;
  color: #f5f6fa;
  margin: 0;
  line-height: 1.5;
}

/* ─── Layout dos columnas (SILL-1523) ─────────────────── */

.ovg-carrito-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  width: 1100px;
  max-width: calc(100% - 40px);
  margin-top: -20px;
}

.ovg-carrito-left {
  flex: 1;
  min-width: 0;
}

.ovg-carrito-right {
  width: 380px;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .ovg-carrito-wrapper {
    flex-direction: column;
  }
  .ovg-carrito-right {
    width: 100%;
  }
}

/* ─── Card base ────────────────────────────────────────── */

.ovg-card {
  position: relative;
  z-index: 1;
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Card estrecha para estados post-solicitud */
.ovg-card--narrow {
  width: 700px;
  max-width: calc(100% - 40px);
  margin-top: -20px;
}

.ovg-card--centrado {
  align-items: center;
  text-align: center;
}

/* ─── Textos ───────────────────────────────────────────── */

.ovg-textos {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ovg-textos__title {
  font-size: 24px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-textos__subtitle {
  font-size: 14px;
  font-weight: 400;
  color: #525252;
  margin: 0;
  line-height: 1.5;
}

/* ─── Datos cliente/equipo ─────────────────────────────── */

.ovg-datos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
}

.ovg-datos__field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ovg-datos__field--full {
  grid-column: 1 / -1;
}

.ovg-datos__label {
  font-size: 14px;
  font-weight: 700;
  color: #23201c;
}

.ovg-datos__label--accent {
  color: #1d4ed8;
}

.ovg-datos__value {
  font-size: 14px;
  color: #525252;
  word-break: break-word;
}

/* ─── Productos ────────────────────────────────────────── */

.ovg-producto-row {
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #444444;
  border-radius: 4px;
  min-height: 72px;
  padding: 10px 16px;
}

.ovg-producto-row__icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.ovg-producto-row__info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.ovg-producto-row__nombre {
  font-size: 14px;
  font-weight: 700;
  color: #23201c;
}

.ovg-producto-row__precio {
  font-size: 18px;
  font-weight: 700;
  color: #203859;
}

/* ─── Panel derecho: Resumen Pago ──────────────────────── */

.ovg-resumen-pago__title {
  font-size: 22px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

/* ─── Cupón (SILL-1523) ────────────────────────────────── */

.ovg-cupon {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ovg-cupon__row {
  display: flex;
  gap: 8px;
}

.ovg-cupon__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 12px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  color: #23201c;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s;
}

.ovg-cupon__input:focus {
  border-color: #1d4ed8;
}

.ovg-cupon__input:disabled {
  background: #f5f6fa;
  color: #525252;
  cursor: not-allowed;
}

.ovg-cupon__btn {
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.ovg-cupon__feedback {
  font-size: 13px;
  margin: 0;
  font-weight: 500;
}

.ovg-cupon__feedback--ok {
  color: #16a34a;
}

.ovg-cupon__feedback--error {
  color: #ef4444;
}

/* ─── Totales ──────────────────────────────────────────── */

.ovg-totales {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
}

.ovg-totales__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ovg-totales__label {
  font-size: 14px;
  font-weight: 400;
  color: #525252;
}

.ovg-totales__value {
  font-size: 14px;
  font-weight: 600;
  color: #23201c;
}

.ovg-totales__row--total {
  border-top: 1px solid #e5e7eb;
  padding-top: 10px;
  margin-top: 4px;
}

.ovg-totales__label--total {
  font-size: 18px;
  font-weight: 700;
  color: #23201c;
}

.ovg-totales__value--total {
  font-size: 22px;
  font-weight: 700;
  color: #0a192f;
}

/* ─── Info aviso ───────────────────────────────────────── */

.ovg-info-aviso {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #dddde1;
  border: 1px solid #525252;
  border-radius: 5px;
  padding: 10px;
}

.ovg-info-aviso__icon {
  font-size: 20px;
  color: #1d4ed8;
  flex-shrink: 0;
  margin-top: 2px;
}

.ovg-info-aviso__text {
  font-size: 13px;
  color: #23201c;
  margin: 0;
  line-height: 1.5;
}

/* ─── Info box ─────────────────────────────────────────── */

.ovg-info-box {
  background: #dddde1;
  border: 1px solid #525252;
  border-radius: 5px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.ovg-info-box--centro {
  align-items: center;
}

.ovg-info-box__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.ovg-info-box__label {
  color: #525252;
  font-weight: 400;
}

.ovg-info-box__value {
  color: #23201c;
  font-weight: 600;
  text-align: right;
}

/* ─── Error genérico ───────────────────────────────────── */

.ovg-error-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ff3838;
}

/* ─── Footer de card ───────────────────────────────────── */

.ovg-card__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.ovg-card__footer--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
}

/* ─── Botones ──────────────────────────────────────────── */

.ovg-link-text {
  background: none;
  border: none;
  color: #23201c;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  align-self: center;
}

.ovg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
}

.ovg-btn:hover {
  opacity: 0.88;
}

.ovg-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Botón oscuro — principal de esta vista */
.ovg-btn--dark {
  background: #0a192f;
  color: #ffffff;
}

/* Botón amarillo — mantenido para compatibilidad con otros estados */
.ovg-btn--primary {
  background: #fece2f;
  color: #23201c;
}

.ovg-btn--secondary {
  background: #f5f6fa;
  color: #23201c;
}

.ovg-btn--block {
  width: 100%;
}

/* ─── Estados post-solicitud ───────────────────────────── */

.ovg-estado__icon {
  font-size: 74px;
  width: 74px;
  height: 74px;
}

.ovg-estado__icon--success {
  color: #22c55e;
}

.ovg-estado__titulo {
  font-size: 22px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-estado__texto {
  font-size: 15px;
  color: #525252;
  margin: 0;
  line-height: 1.5;
  max-width: 480px;
}

/* ─── Confirmación de solicitud ────────────────────────── */

.ovg-exito__icon {
  align-self: center;
}

.ovg-exito__textos {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.ovg-exito__titulo {
  font-size: 28px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-exito__texto {
  font-size: 15px;
  font-weight: 400;
  color: #525252;
  margin: 0;
  line-height: 1.5;
}

.ovg-exito__serie {
  font-weight: 700;
  color: #1d4ed8;
}

.ovg-exito__subtitulo {
  font-size: 18px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-exito__lista {
  margin: 0;
  padding-left: 22px;
  color: #525252;
  font-size: 15px;
  list-style: disc;
}

.ovg-exito__lista li {
  line-height: 25px;
}

/* ─── Footer de página ─────────────────────────────────── */

.ovg-page__footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  font-size: 15px;
  font-weight: 400;
  color: #23201c;
  text-align: center;
  margin: 0;
  padding: 12px 0;
  border-top: 1px solid #d2d2d2;
  background: #f5f6fa;
}

/* ============================================================
   MOBILE — max-width: 767px
   ============================================================ */
@media (max-width: 767px) {
  /* Page */
  .ovg-page {
    padding-bottom: 60px;
  }

  /* Header */
  .ovg-header {
    padding: 0 20px;
  }

  /* Sub-header */
  .ovg-subheader {
    min-height: auto;
    padding: 24px 20px;
  }

  .ovg-subheader__title {
    font-size: 22px;
  }

  .ovg-subheader__subtitle {
    font-size: 14px;
  }

  /* Card */
  .ovg-card {
    width: calc(100% - 32px);
    border-radius: 16px;
  }

  /* Datos grid → columna única */
  .ovg-datos {
    grid-template-columns: 1fr;
  }

  .ovg-datos__field--full {
    grid-column: 1;
  }

  /* Botón "Solicitar compra" → azul marino en mobile (Figma #203859) */
  .ovg-btn--solicitar {
    background: #203859;
    color: #ffffff;
  }

  /* Producto row */
  .ovg-producto-row {
    padding: 10px;
    gap: 12px;
  }

  /* Footer footer → visible en mobile (no hay bottom bar aquí) */
}
</style>
